import { useQuery } from '@tanstack/react-query';
import creatorProgramAPI from '../../../APIs/creatorProgramAPI';
import {
  PERFORMANCE_STALE_TIME_MS,
  QUERY_KEYS,
  STALE_TIME_MS,
} from '../constants';
import type {
  Attribution,
  AttributionParams,
  Payout,
  PayoutParams,
  Creator,
  CreatorPerformance,
  LeaderboardResponse,
  PaginatedResponse,
  PerformanceParams,
} from '../types';

/**
 * React Query hooks for the Creator Program.
 *
 * The API module already unwraps the `{ success, data }` envelope, so these return
 * the payload directly. Query keys carry every parameter that changes the result, so
 * changing a filter refetches rather than filtering an already-fetched page.
 */

/**
 * §2.2 — paginated creators list.
 *
 * `placeholderData` keeps the previous page on screen while the next one loads,
 * matching `Items/hooks/useItemsQuery.ts`, so the table doesn't blank out on paging.
 */
export function useCreators(page: number, limit: number) {
  return useQuery<PaginatedResponse<Creator>>({
    queryKey: [QUERY_KEYS.CREATORS, page, limit],
    queryFn: () => creatorProgramAPI.listCreators({ page, limit }),
    placeholderData: (previous) => previous,
    staleTime: STALE_TIME_MS,
  });
}

/**
 * §2.3 — one creator, with full `terms` history and `codes` attached.
 *
 * Note the `terms` array is the complete version history, not "current terms".
 * Resolving the active row is the detail/terms screens' job (docs §1.1).
 */
export function useCreator(id?: string) {
  return useQuery<Creator>({
    queryKey: [QUERY_KEYS.CREATOR, id],
    queryFn: () => creatorProgramAPI.getCreator(id as string),
    enabled: !!id,
    staleTime: STALE_TIME_MS,
  });
}

/**
 * §3.1 — leaderboard across all creators.
 *
 * The server sorts by `summary.netCreatorEarnings` DESC and *then* paginates, so
 * `total`/`page`/`limit` describe that pagination rather than a raw row count. The
 * default order is "who cost the most this period", which is the report's whole
 * point — never re-sort the returned rows client-side.
 *
 * Held with no client-side stale window (§1.5) — see `PERFORMANCE_STALE_TIME_MS`.
 */
export function useLeaderboard(params: PerformanceParams = {}) {
  const { startDate, endDate, store, page, limit } = params;

  return useQuery<LeaderboardResponse>({
    queryKey: [QUERY_KEYS.LEADERBOARD, startDate, endDate, store, page, limit],
    queryFn: () => creatorProgramAPI.getLeaderboard(params),
    placeholderData: (previous) => previous,
    staleTime: PERFORMANCE_STALE_TIME_MS,
  });
}

/**
 * §3.2 — one creator's numbers over a date range.
 *
 * The range and store live in the query key, so changing either refetches rather
 * than filtering client-side — the docs are explicit that this response is not
 * client-paginated or cached, and that one call already returns everything the
 * screen needs (summary, byStore, byCode, links).
 *
 * The server caches these for up to an hour per range/store/creator combination but
 * invalidates immediately on new orders or cancellations, so the figures are already
 * near-real-time at the source (§6). We therefore add no stale window of our own —
 * see `PERFORMANCE_STALE_TIME_MS` — and need no manual cache-busting either.
 */
export function useCreatorPerformance(id?: string, params: PerformanceParams = {}) {
  const { startDate, endDate, store } = params;

  return useQuery<CreatorPerformance>({
    queryKey: [QUERY_KEYS.CREATOR_PERFORMANCE, id, startDate, endDate, store],
    queryFn: () => creatorProgramAPI.getCreatorPerformance(id as string, params),
    enabled: !!id,
    placeholderData: (previous) => previous,
    staleTime: PERFORMANCE_STALE_TIME_MS,
  });
}

/**
 * §4 — order-level attribution ledger.
 *
 * All filters (`creator`, `store`, `status`, date range) are ANDed together
 * server-side, so they go into the query key and each change refetches rather than
 * narrowing an already-fetched page — filtering client-side would only ever filter
 * the current page, not the whole ledger.
 */
export function useAttributions(params: AttributionParams = {}) {
  const { creator, store, status, startDate, endDate, page, limit } = params;

  return useQuery<PaginatedResponse<Attribution>>({
    queryKey: [
      QUERY_KEYS.ATTRIBUTIONS,
      creator,
      store,
      status,
      startDate,
      endDate,
      page,
      limit,
    ],
    queryFn: () => creatorProgramAPI.listAttributions(params),
    placeholderData: (previous) => previous,
    staleTime: STALE_TIME_MS,
  });
}

/**
 * §5.2 — payouts list, optionally filtered by creator and status.
 */
export function usePayouts(params: PayoutParams = {}) {
  const { creator, status, page, limit } = params;

  return useQuery<PaginatedResponse<Payout>>({
    queryKey: [QUERY_KEYS.PAYOUTS, creator, status, page, limit],
    queryFn: () => creatorProgramAPI.listPayouts(params),
    placeholderData: (previous) => previous,
    staleTime: STALE_TIME_MS,
  });
}

/**
 * §5.3 — one payout, including the exact `attributions[]` rows it swept up.
 *
 * Only fetched when a payout is actually opened, since the row list can be large and
 * the list endpoint doesn't carry it.
 */
export function usePayout(id?: string) {
  return useQuery<Payout>({
    queryKey: [QUERY_KEYS.PAYOUT, id],
    queryFn: () => creatorProgramAPI.getPayout(id as string),
    enabled: !!id,
    staleTime: STALE_TIME_MS,
  });
}
